/**
 * Currency Formatting Utilities
 * 
 * REQUIREMENTS:
 * - Centralized currency formatting to avoid duplication
 * - Consistent USD formatting across the application
 * - Support both dollar amounts and cent amounts
 * 
 * Usage:
 * - formatCurrency(100.50) => "$100.50"
 * - formatCurrencyFromCents(10050) => "$100.50"
 * - formatCurrencyNoDecimals(100) => "$100"
 */

/**
 * Format a number as USD currency with 2 decimal places
 * @param amount - The amount in dollars
 * @returns Formatted currency string (e.g., "$100.50")
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a number as USD currency with no decimal places
 * Useful for display when cents are not relevant
 * @param amount - The amount in dollars
 * @returns Formatted currency string (e.g., "$100")
 */
export function formatCurrencyNoDecimals(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format a dollar amount showing cents ONLY when the amount has them:
 * whole-dollar amounts render clean ("$40"), cents amounts render exact
 * ("$47.50" — never rounded to "$48"). Use anywhere a displayed price must
 * match the exact amount charged (e.g. private lesson duration prices,
 * which accept cents).
 * @param amount - The amount in dollars
 * @returns Formatted currency string (e.g., "$40" or "$47.50")
 */
export function formatCurrencyWholeOrCents(amount: number): string {
  return amount % 1 === 0 ? formatCurrencyNoDecimals(amount) : formatCurrency(amount);
}

/**
 * Format amount from cents to dollars for display
 * Used when working with Stripe amounts (which are in cents)
 * @param amountInCents - The amount in cents
 * @returns Formatted currency string (e.g., "$100.50")
 */
export function formatCurrencyFromCents(amountInCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInCents / 100);
}

/**
 * Parse a currency string to a number
 * Removes currency symbols and formatting
 * @param currencyString - The formatted currency string (e.g., "$100.50")
 * @returns The numeric value (e.g., 100.50)
 */
export function parseCurrency(currencyString: string): number {
  const cleaned = currencyString.replace(/[^0-9.-]+/g, '');
  return parseFloat(cleaned) || 0;
}
